import { useEffect, useMemo, useRef, useState } from "react";
import {
  cardStyle,
  ghostButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  SectionTitle,
  inputStyle,
} from "../dashboardStyles";
import { api } from "../../services/api";
import { formatCapturedAt, formatCurrency, formatDistance, normalizeTextKey } from "../dashboardUtils";

function buildGoogleMapsUrl(market) {
  if (!market) {
    return "";
  }

  const query =
    String(market.address || "").trim() ||
    (Number.isFinite(Number(market.latitude)) && Number.isFinite(Number(market.longitude))
      ? `${market.latitude},${market.longitude}`
      : "");

  if (!query) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function getShoppingListMarketKey(market) {
  if (!market) {
    return "";
  }

  return String(market.market_id || market.market || market.address || "");
}

function getActiveListLine(value, cursorIndex) {
  const listValue = String(value || "");
  const safeCursorIndex = Math.min(
    Math.max(Number(cursorIndex) || 0, 0),
    listValue.length
  );
  const lineStart = listValue.slice(0, safeCursorIndex).lastIndexOf("\n") + 1;
  const nextBreakIndex = listValue.indexOf("\n", safeCursorIndex);
  const lineEnd = nextBreakIndex === -1 ? listValue.length : nextBreakIndex;

  return {
    end: lineEnd,
    start: lineStart,
    text: listValue.slice(lineStart, lineEnd),
  };
}

function replaceActiveListLine(value, cursorIndex, nextLine) {
  const listValue = String(value || "");
  const activeLine = getActiveListLine(listValue, cursorIndex);
  const prefix = listValue.slice(0, activeLine.start);
  const suffix = listValue.slice(activeLine.end);
  const nextValue = `${prefix}${nextLine}${suffix}`;

  return {
    cursorIndex: prefix.length + nextLine.length,
    value: nextValue,
  };
}

function buildProductNameSuggestions(responseData) {
  const seenNames = new Set();

  return (responseData?.products || [])
    .map((product) => String(product?.product || product?.name || "").trim())
    .filter(Boolean)
    .filter((productName) => {
      const normalizedName = normalizeTextKey(productName);
      if (!normalizedName || seenNames.has(normalizedName)) {
        return false;
      }
      seenNames.add(normalizedName);
      return true;
    })
    .slice(0, 6);
}

function getEstimatedTotal(market) {
  const parsedTotal = Number(market?.estimated_total);
  return Number.isFinite(parsedTotal) ? parsedTotal : null;
}

function getMarketItemsFound(market) {
  const foundCount = Number(market?.found_items_count);
  const missingCount = Number(market?.missing_items_count);

  if (!Number.isFinite(foundCount)) {
    return null;
  }

  return {
    found: foundCount,
    total: foundCount + (Number.isFinite(missingCount) ? missingCount : 0),
  };
}

function collectShareComparisonMarkets(shoppingResult, compareResult) {
  const candidates = [
    shoppingResult?.best_market,
    shoppingResult?.nearest_market,
    shoppingResult?.best_value_market,
    ...(compareResult?.evaluated_markets || []),
  ].filter(Boolean);
  const seenKeys = new Set();

  return candidates.filter((market) => {
    const key = getShoppingListMarketKey(market);
    if (!key || seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return getEstimatedTotal(market) !== null;
  });
}

function calculateShareSavingsAmount(bestMarket, shoppingResult, compareResult) {
  const bestTotal = getEstimatedTotal(bestMarket);
  if (bestTotal === null) {
    return null;
  }

  const totals = collectShareComparisonMarkets(shoppingResult, compareResult)
    .map(getEstimatedTotal)
    .filter((value) => value !== null);

  if (totals.length < 2) {
    return null;
  }

  const highestTotal = Math.max(...totals);
  const savingsAmount = highestTotal - bestTotal;
  return savingsAmount > 0.01 ? savingsAmount : null;
}

function getShareUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  const origin = String(window.location?.origin || "");
  if (!origin || origin === "null" || origin.startsWith("capacitor://")) {
    return "";
  }

  return origin;
}

function buildSavingsShareText({ bestMarket, compareResult, shoppingResult, t }) {
  if (!bestMarket) {
    return "";
  }

  const estimatedTotal = getEstimatedTotal(bestMarket);
  const savingsAmount = calculateShareSavingsAmount(bestMarket, shoppingResult, compareResult);
  const itemCount = getMarketItemsFound(bestMarket);
  const totalText =
    estimatedTotal !== null
      ? formatCurrency(estimatedTotal)
      : t?.("common.no_data", "n/d") || "n/d";
  const savingsText = savingsAmount !== null ? formatCurrency(savingsAmount) : "";
  const itemText =
    itemCount && itemCount.total > 0
      ? t?.(
          "list.share.items_found",
          "{{found}} de {{total}} itens encontrados",
          itemCount
        ) || `${itemCount.found} de ${itemCount.total} itens encontrados`
      : "";

  const intro =
    savingsAmount !== null
      ? t?.(
          "list.share.post_with_savings",
          "Eu economizei ate {{savings}} usando o PennyPantry.",
          { savings: savingsText }
        ) || `Eu economizei ate ${savingsText} usando o PennyPantry.`
      : t?.(
          "list.share.post_without_savings",
          "Eu achei o melhor mercado para minha lista usando o PennyPantry."
        ) || "Eu achei o melhor mercado para minha lista usando o PennyPantry.";

  const marketLine =
    t?.(
      "list.share.market_line",
      "Minha compra ficou por {{total}} em {{market}}.",
      { market: bestMarket.market, total: totalText }
    ) || `Minha compra ficou por ${totalText} em ${bestMarket.market}.`;
  const itemLine = itemText ? `${itemText}.` : "";
  const distanceLine =
    bestMarket.distance_km !== null && bestMarket.distance_km !== undefined
      ? `${t?.("common.distance", "Distancia") || "Distancia"}: ${formatDistance(bestMarket.distance_km)}.`
      : "";
  const callToAction =
    t?.(
      "list.share.call_to_action",
      "Compare antes de comprar. #PennyPantry #Economia"
    ) || "Compare antes de comprar. #PennyPantry #Economia";

  return [intro, marketLine, itemLine, distanceLine, callToAction].filter(Boolean).join("\n");
}

async function copyTextToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "-1000px";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();

  let copied = false;
  try {
    copied = Boolean(document.execCommand?.("copy"));
  } finally {
    document.body.removeChild(textArea);
  }

  return copied;
}

function ResultCard({ label, market, onOpen, t }) {
  if (!market) {
    return (
      <div>
        <div style={{ color: "#7c2d12" }}>{label}</div>
        <strong>{t?.("common.no_data", "n/d") || "n/d"}</strong>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(market)}
      style={{
        textAlign: "left",
        border: "1px solid #fed7aa",
        borderRadius: 18,
        padding: 14,
        background: "#fffdf8",
        cursor: "pointer",
      }}
    >
      <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
        {label}
      </div>
      <strong style={{ display: "block", marginTop: 6 }}>{market.market}</strong>
      <div style={{ marginTop: 6, color: "#ef4444", fontWeight: 800, fontSize: 22 }}>
        {formatCurrency(market.estimated_total)}
      </div>
      <div style={{ marginTop: 6, color: "#7c2d12" }}>
        {(t?.("list.comparison.distance", "Distancia") || "Distancia") + ": "}
        {formatDistance(market.distance_km)}
      </div>
    </button>
  );
}

function ShareSavingsCard({ compareResult, shoppingResult, t }) {
  const [shareStatus, setShareStatus] = useState("");
  const bestMarket = shoppingResult?.best_market;
  const shareUrl = getShareUrl();
  const shareText = useMemo(
    () => buildSavingsShareText({ bestMarket, compareResult, shoppingResult, t }),
    [bestMarket, compareResult, shoppingResult, t]
  );
  const encodedText = encodeURIComponent([shareText, shareUrl].filter(Boolean).join("\n"));

  if (!bestMarket || !shareText) {
    return null;
  }

  const markCopied = () => {
    setShareStatus(t?.("list.share.copied", "Texto copiado. Agora e so colar no post.") || "Texto copiado. Agora e so colar no post.");
  };

  const openExternalShare = async (url, { copyFirst = false } = {}) => {
    if (copyFirst) {
      await copyTextToClipboard(shareText);
      markCopied();
    }

    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleNativeShare = async () => {
    const title = t?.("list.share.native_title", "Minha economia no PennyPantry") || "Minha economia no PennyPantry";
    const payload = {
      title,
      text: shareText,
      ...(shareUrl ? { url: shareUrl } : {}),
    };

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(payload);
        setShareStatus(t?.("list.share.opened", "Compartilhamento aberto.") || "Compartilhamento aberto.");
        return;
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
      }
    }

    await copyTextToClipboard(shareText);
    markCopied();
  };

  const handleCopy = async () => {
    await copyTextToClipboard(shareText);
    markCopied();
  };

  return (
    <div
      style={{
        marginTop: 18,
        padding: 16,
        borderRadius: 22,
        border: "1px solid #fdba74",
        background:
          "linear-gradient(135deg, rgba(255, 247, 237, 0.98), rgba(254, 243, 199, 0.92))",
        boxShadow: "0 16px 40px rgba(234, 88, 12, 0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
            {t?.("list.share.eyebrow", "Post pronto") || "Post pronto"}
          </div>
          <h3 style={{ margin: "6px 0 0" }}>
            {t?.("list.share.title", "Compartilhe sua economia") || "Compartilhe sua economia"}
          </h3>
        </div>
        <button type="button" onClick={handleNativeShare} style={primaryButtonStyle}>
          {t?.("list.share.main_button", "Compartilhar economia") || "Compartilhar economia"}
        </button>
      </div>

      <pre
        style={{
          whiteSpace: "pre-wrap",
          margin: "14px 0 0",
          padding: 14,
          borderRadius: 18,
          border: "1px solid #fed7aa",
          background: "#fffdf8",
          color: "#431407",
          fontFamily: "inherit",
          lineHeight: 1.45,
        }}
      >
        {shareText}
      </pre>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
        <button
          type="button"
          onClick={() => openExternalShare(`https://wa.me/?text=${encodedText}`)}
          style={ghostButtonStyle}
        >
          WhatsApp
        </button>
        <button
          type="button"
          onClick={() =>
            openExternalShare(
              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl || "https://www.facebook.com")}&quote=${encodeURIComponent(shareText)}`
            )
          }
          style={ghostButtonStyle}
        >
          Facebook
        </button>
        <button
          type="button"
          onClick={() => openExternalShare("https://www.instagram.com/", { copyFirst: true })}
          style={ghostButtonStyle}
        >
          Instagram
        </button>
        <button
          type="button"
          onClick={() => openExternalShare("https://www.tiktok.com/upload", { copyFirst: true })}
          style={ghostButtonStyle}
        >
          TikTok
        </button>
        <button type="button" onClick={handleCopy} style={secondaryButtonStyle}>
          {t?.("list.share.copy", "Copiar texto") || "Copiar texto"}
        </button>
      </div>

      <div style={{ marginTop: 10, color: "#9a3412", fontSize: 13 }}>
        {shareStatus ||
          t?.(
            "list.share.helper",
            "No celular, o botao principal abre a folha nativa para escolher Instagram, TikTok, WhatsApp, Facebook ou outro app."
          ) ||
          "No celular, o botao principal abre a folha nativa para escolher Instagram, TikTok, WhatsApp, Facebook ou outro app."}
      </div>
    </div>
  );
}

export default function ListPage({
  authHeaders,
  openMarketPage,
  calculateBestRoute,
  calculateShoppingList,
  compareResult,
  compareShoppingList,
  createShoppingListAlert,
  configuredDiscoveryRadiusKmLabel,
  distanceUnitLabel = "km",
  marketCatalog,
  routeResult,
  removeShoppingListAlert,
  setShoppingListAlertMaxDistanceKm,
  setShoppingListAlertTargetTotal,
  setShoppingListText,
  shoppingListAlertMaxDistanceKm,
  shoppingListAlertTargetTotal,
  shoppingListAlerts,
  shoppingListText,
  shoppingResult,
  t,
  toggleFavorite,
  userLocation,
}) {
  const shoppingListInputRef = useRef(null);
  const listLookupTimeoutRef = useRef(null);
  const [selectedListMarketKey, setSelectedListMarketKey] = useState("");
  const [listCursorIndex, setListCursorIndex] = useState(0);
  const [listProductSuggestions, setListProductSuggestions] = useState([]);
  const [isSearchingListSuggestions, setIsSearchingListSuggestions] = useState(false);
  const [selectedListSuggestionLabel, setSelectedListSuggestionLabel] = useState("");
  const summaryMarkets = useMemo(
    () =>
      [shoppingResult?.best_market, shoppingResult?.nearest_market, shoppingResult?.best_value_market]
        .filter(Boolean)
        .reduce((accumulator, market) => {
          const key = getShoppingListMarketKey(market);
          if (!key || accumulator.some((item) => getShoppingListMarketKey(item) === key)) {
            return accumulator;
          }
          accumulator.push(market);
          return accumulator;
        }, []),
    [shoppingResult]
  );

  const selectableListMarkets = useMemo(() => {
    const candidates = [
      ...summaryMarkets,
      compareResult?.cheapest_market,
      compareResult?.nearest_market,
      compareResult?.best_balance_market,
      ...(compareResult?.evaluated_markets || []),
      routeResult?.recommended_route,
      routeResult?.cheapest_route,
      routeResult?.nearest_route,
      routeResult?.balanced_route,
    ].filter(Boolean);
    const seenKeys = new Set();

    return candidates.filter((market) => {
      const key = getShoppingListMarketKey(market);
      if (!key || seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
  }, [compareResult, routeResult, summaryMarkets]);

  const selectedListMarket =
    selectableListMarkets.find((market) => getShoppingListMarketKey(market) === selectedListMarketKey) ||
    null;

  const selectedMapsUrl = useMemo(
    () => buildGoogleMapsUrl(selectedListMarket),
    [selectedListMarket]
  );
  const activeListLineQuery = useMemo(
    () => getActiveListLine(shoppingListText, listCursorIndex).text.trim(),
    [listCursorIndex, shoppingListText]
  );

  useEffect(() => {
    const trimmedQuery = activeListLineQuery.trim();
    const normalizedQuery = normalizeTextKey(trimmedQuery);
    const normalizedSelectedSuggestion = normalizeTextKey(selectedListSuggestionLabel);

    if (listLookupTimeoutRef.current && typeof window !== "undefined") {
      window.clearTimeout(listLookupTimeoutRef.current);
      listLookupTimeoutRef.current = null;
    }

    if (
      trimmedQuery.length < 2 ||
      (normalizedSelectedSuggestion && normalizedQuery === normalizedSelectedSuggestion)
    ) {
      setIsSearchingListSuggestions(false);
      setListProductSuggestions([]);
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    let isCurrentLookup = true;
    listLookupTimeoutRef.current = window.setTimeout(async () => {
      setIsSearchingListSuggestions(true);
      try {
        const response = await api.get("/search/unified", {
          params: {
            q: trimmedQuery,
            user_latitude: userLocation?.latitude,
            user_longitude: userLocation?.longitude,
            limit: 8,
          },
          headers: authHeaders || undefined,
        });

        if (isCurrentLookup) {
          setListProductSuggestions(buildProductNameSuggestions(response.data));
        }
      } catch {
        if (isCurrentLookup) {
          setListProductSuggestions([]);
        }
      } finally {
        if (isCurrentLookup) {
          setIsSearchingListSuggestions(false);
        }
      }
    }, 280);

    return () => {
      isCurrentLookup = false;
      if (listLookupTimeoutRef.current) {
        window.clearTimeout(listLookupTimeoutRef.current);
        listLookupTimeoutRef.current = null;
      }
    };
  }, [activeListLineQuery, authHeaders, selectedListSuggestionLabel, userLocation]);

  useEffect(() => {
    const fallbackMarket =
      shoppingResult?.best_market ||
      compareResult?.cheapest_market ||
      compareResult?.evaluated_markets?.[0] ||
      routeResult?.recommended_route ||
      selectableListMarkets[0] ||
      null;
    const fallbackKey = getShoppingListMarketKey(fallbackMarket);
    if (!fallbackKey) {
      setSelectedListMarketKey("");
      return;
    }

    setSelectedListMarketKey((current) => {
      const hasCurrentMarket = selectableListMarkets.some(
        (market) => getShoppingListMarketKey(market) === current
      );
      return hasCurrentMarket ? current : fallbackKey;
    });
  }, [
    compareResult?.cheapest_market,
    compareResult?.evaluated_markets,
    routeResult?.recommended_route,
    selectableListMarkets,
    shoppingResult?.best_market,
  ]);

  const openListMarketDetail = (market) => {
    const marketKey = getShoppingListMarketKey(market);
    if (!marketKey) {
      return;
    }
    setSelectedListMarketKey(marketKey);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openInGoogleMaps = () => {
    if (!selectedMapsUrl || typeof window === "undefined") {
      return;
    }
    window.location.assign(selectedMapsUrl);
  };

  const updateListCursorFromEvent = (event) => {
    setListCursorIndex(
      event.currentTarget.selectionStart ?? event.currentTarget.value.length
    );
  };

  const handleShoppingListTextChange = (event) => {
    setShoppingListText(event.target.value);
    setListCursorIndex(event.target.selectionStart ?? event.target.value.length);
    if (normalizeTextKey(event.target.value) !== normalizeTextKey(selectedListSuggestionLabel)) {
      setSelectedListSuggestionLabel("");
    }
  };

  const applyListSuggestion = (productName) => {
    const replacement = replaceActiveListLine(
      shoppingListText,
      listCursorIndex,
      productName
    );
    setShoppingListText(replacement.value);
    setListCursorIndex(replacement.cursorIndex);
    setSelectedListSuggestionLabel(productName);
    setListProductSuggestions([]);
    setIsSearchingListSuggestions(false);

    const restoreTextareaFocus = () => {
      const input = shoppingListInputRef.current;
      if (!input) {
        return;
      }
      input.focus();
      input.setSelectionRange(replacement.cursorIndex, replacement.cursorIndex);
    };

    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(restoreTextareaFocus);
      return;
    }

    restoreTextareaFocus();
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ ...cardStyle, padding: 24 }}>
        <SectionTitle
          eyebrow={t?.("list.hero.eyebrow", "Lista inteligente") || "Lista inteligente"}
          title={t?.("list.hero.title", "Monte a compra e deixe o app decidir") || "Monte a compra e deixe o app decidir"}
          description={
            t?.(
              "list.hero.description",
              "Cole a lista uma vez e veja primeiro o mercado mais barato, o mais perto e a melhor combinacao de custo-beneficio."
            ) || "Cole a lista uma vez e veja primeiro o mercado mais barato, o mais perto e a melhor combinacao de custo-beneficio."
          }
        />

        <textarea
          ref={shoppingListInputRef}
          style={{ ...inputStyle, minHeight: 160, resize: "vertical" }}
          placeholder={
            t?.("list.hero.placeholder", "Um produto por linha\nArroz 5kg\nFeijao 1kg\nCafe 500g") ||
            "Um produto por linha\nArroz 5kg\nFeijao 1kg\nCafe 500g"
          }
          value={shoppingListText}
          onChange={handleShoppingListTextChange}
          onClick={updateListCursorFromEvent}
          onKeyUp={updateListCursorFromEvent}
          onSelect={updateListCursorFromEvent}
        />

        {(isSearchingListSuggestions || listProductSuggestions.length > 0) && (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              borderRadius: 18,
              border: "1px solid #fed7aa",
              background: "#fffdf8",
            }}
          >
            <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 800 }}>
              {isSearchingListSuggestions
                ? t?.("list.suggestions.loading", "Buscando produtos...") || "Buscando produtos..."
                : t?.("list.suggestions.title", "Sugestoes de produtos") || "Sugestoes de produtos"}
            </div>
            {listProductSuggestions.length > 0 ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {listProductSuggestions.map((productName) => (
                  <button
                    key={productName}
                    type="button"
                    onClick={() => applyListSuggestion(productName)}
                    style={{
                      border: "1px solid #fdba74",
                      borderRadius: 999,
                      background: "#ffffff",
                      color: "#7c2d12",
                      cursor: "pointer",
                      fontWeight: 800,
                      padding: "9px 12px",
                    }}
                  >
                    {productName}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
          <button onClick={calculateShoppingList} style={primaryButtonStyle}>
            {t?.("list.hero.best_market", "Melhor mercado") || "Melhor mercado"}
          </button>
          <button onClick={compareShoppingList} style={ghostButtonStyle}>
            {t?.("list.hero.compare_markets", "Comparar mercados") || "Comparar mercados"}
          </button>
          <button onClick={calculateBestRoute} style={secondaryButtonStyle}>
            {t?.("list.hero.best_route", "Melhor rota") || "Melhor rota"}
          </button>
          {shoppingResult?.shopping_list_id && (
            <button
              onClick={() =>
                toggleFavorite({
                  entity_type: "shopping_list",
                  entity_id: shoppingResult.shopping_list_id,
                  title: "Lista inteligente",
                  subtitle: `${shoppingResult.requested_products?.length || 0} itens`,
                })
              }
              style={ghostButtonStyle}
            >
              {t?.("list.hero.favorite", "Favoritar lista") || "Favoritar lista"}
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
        }}
      >
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>
            {t?.("list.summary.title", "Resumo da lista") || "Resumo da lista"}
          </h3>
          {shoppingResult?.best_market ? (
            <div style={{ display: "grid", gap: 12 }}>
              <ResultCard
                label={t?.("list.summary.cheapest", "Mais barato") || "Mais barato"}
                market={shoppingResult.best_market}
                onOpen={openListMarketDetail}
                t={t}
              />
              <ResultCard
                label={t?.("list.summary.closest", "Mais proximo") || "Mais proximo"}
                market={shoppingResult.nearest_market}
                onOpen={openListMarketDetail}
                t={t}
              />
              <ResultCard
                label={t?.("list.summary.best_value", "Melhor equilibrio") || "Melhor equilibrio"}
                market={shoppingResult.best_value_market}
                onOpen={openListMarketDetail}
                t={t}
              />
            </div>
          ) : (
            <div style={{ color: "#9a3412" }}>
              {t?.("list.summary.empty", "Calcule a lista para ver qual mercado vence.") ||
                "Calcule a lista para ver qual mercado vence."}
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>
            {t?.("list.comparison.title", "Comparativo detalhado") || "Comparativo detalhado"}
          </h3>
          {compareResult?.evaluated_markets?.length > 0 ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ color: "#9a3412", fontSize: 13, fontWeight: 700 }}>
                {t?.("list.comparison.count", "{{count}} mercados avaliados", {
                  count: compareResult.evaluated_markets.length,
                }) || `${compareResult.evaluated_markets.length} mercados avaliados`}
              </div>
              {compareResult.evaluated_markets.slice(0, 8).map((market) => (
                <button
                  key={market.market_id}
                  onClick={() => openListMarketDetail(market)}
                  style={{
                    textAlign: "left",
                    padding: 14,
                    borderRadius: 18,
                    border: "1px solid #fed7aa",
                    background: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  <strong>{market.market}</strong>
                  <div style={{ marginTop: 6, color: "#7c2d12" }}>
                    {(t?.("list.comparison.total", "Total estimado") || "Total estimado") + ": "}
                    {formatCurrency(market.estimated_total)}
                  </div>
                  <div style={{ marginTop: 6, color: "#ea580c" }}>
                    {(t?.("list.comparison.distance", "Distancia") || "Distancia") + ": "}
                    {formatDistance(market.distance_km)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9a3412" }}>
              {t?.("list.comparison.empty", "Use o botao de comparacao para ver todos os mercados avaliados.") ||
                "Use o botao de comparacao para ver todos os mercados avaliados."}
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>
            {t?.("list.route.title", "Rota economica") || "Rota economica"}
          </h3>
          {routeResult?.recommended_route ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <div style={{ color: "#7c2d12", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                  {t?.("list.route.main", "Recomendacao principal") || "Recomendacao principal"}
                </div>
                <button
                  type="button"
                  onClick={() => openListMarketDetail(routeResult.recommended_route)}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    marginTop: 6,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "inherit",
                    color: "#431407",
                    textAlign: "left",
                  }}
                >
                  {routeResult.recommended_route.market}
                </button>
              </div>
              <div style={{ color: "#ef4444", fontWeight: 800, fontSize: 24 }}>
                {formatCurrency(routeResult.recommended_route.estimated_total)}
              </div>
              <div style={{ color: "#7c2d12" }}>
                {(t?.("list.comparison.distance", "Distancia") || "Distancia") + ": "}
                {formatDistance(routeResult.recommended_route.distance_km)}
              </div>
              <div style={{ color: "#9a3412" }}>{routeResult.recommended_route.reason}</div>
            </div>
          ) : (
            <div style={{ color: "#9a3412" }}>
              {t?.("list.route.empty", "Calcule a melhor rota para combinar preco e deslocamento.") ||
                "Calcule a melhor rota para combinar preco e deslocamento."}
            </div>
          )}
        </div>
      </div>

      {selectedListMarket ? (
        <div style={{ ...cardStyle, border: "1px solid #fdba74", background: "#fffdf8" }}>
          <SectionTitle
            eyebrow={t?.("list.market_detail.eyebrow", "Mercado da lista") || "Mercado da lista"}
            title={selectedListMarket.market}
            description={
              selectedListMarket.address ||
              t?.("list.market_detail.address_missing", "Endereco ainda nao informado") ||
              "Endereco ainda nao informado"
            }
            action={
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {selectedMapsUrl ? (
                  <button onClick={openInGoogleMaps} style={primaryButtonStyle}>
                    {t?.("list.market_detail.maps", "Abrir no Google Maps") || "Abrir no Google Maps"}
                  </button>
                ) : null}
                <button
                  onClick={() => {
                    const matchedMarket =
                      marketCatalog.find((item) => item.market_id === selectedListMarket.market_id) ||
                      selectedListMarket;
                    openMarketPage(matchedMarket, {
                      statusMessage:
                        t?.("status.market.open.list", "{{market}} aberto pela lista inteligente.", {
                          market: matchedMarket.market,
                        }) || `${matchedMarket.market} aberto pela lista inteligente.`,
                    });
                  }}
                  style={ghostButtonStyle}
                >
                  {t?.("list.market_detail.open_market", "Abrir pagina do mercado") ||
                    "Abrir pagina do mercado"}
                </button>
              </div>
            }
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <ResultCard
              label={t?.("list.market_detail.total", "Total estimado") || "Total estimado"}
              market={selectedListMarket}
              onOpen={() => {}}
              t={t}
            />
            <div
              style={{
                padding: 14,
                borderRadius: 18,
                border: "1px solid #fed7aa",
                background: "#ffffff",
              }}
            >
              <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                {t?.("list.market_detail.availability", "Disponibilidade") || "Disponibilidade"}
              </div>
              <strong style={{ display: "block", marginTop: 8, fontSize: 24 }}>
                {selectedListMarket.found_items_count ?? 0}/
                {(selectedListMarket.found_items_count ?? 0) +
                  (selectedListMarket.missing_items_count ?? 0)}
              </strong>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {(selectedListMarket.items || []).slice(0, 6).map((item) => (
              <div
                key={`${selectedListMarket.market_id || selectedListMarket.market}-${item.requested_product}`}
                style={{
                  padding: 14,
                  borderRadius: 18,
                  border: "1px solid #fed7aa",
                  background: item.found ? "#ffffff" : "#fff7ed",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>{item.requested_product}</strong>
                  <span
                    style={{
                      fontWeight: 700,
                      color: item.found ? "#0f766e" : "#b45309",
                    }}
                  >
                    {item.found
                      ? t?.("list.market_detail.found", "Encontrado") || "Encontrado"
                      : t?.("list.market_detail.missing", "Nao encontrado") || "Nao encontrado"}
                  </span>
                </div>
                <div style={{ marginTop: 8, color: "#7c2d12" }}>
                  {item.canonical_product || item.requested_product}
                </div>
                <div style={{ marginTop: 8, color: "#9a3412" }}>
                  {(t?.("list.market_detail.estimated_item_total", "Total estimado do item") ||
                    "Total estimado do item") + ": "}
                  {item.estimated_total !== null && item.estimated_total !== undefined
                    ? formatCurrency(item.estimated_total)
                    : t?.("common.no_data", "n/d") || "n/d"}
                </div>
              </div>
            ))}
          </div>

          {selectedListMarket.missing_products?.length > 0 ? (
            <div style={{ marginTop: 16, color: "#b45309" }}>
              {(t?.("list.market_detail.missing_list", "Itens faltando") || "Itens faltando") + ": "}
              {selectedListMarket.missing_products.join(", ")}
            </div>
          ) : null}

          <ShareSavingsCard
            compareResult={compareResult}
            shoppingResult={shoppingResult}
            t={t}
          />
        </div>
      ) : null}

      {shoppingResult?.shopping_list_id && (
        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("list.alerts.eyebrow", "Alertas") || "Alertas"}
            title={t?.("list.alerts.active_title", "Listas monitoradas") || "Listas monitoradas"}
            description={
              t?.(
                "list.alerts.active_description",
                "Ative um alerta para ser avisado quando o total da compra cair abaixo da sua meta."
              ) || "Ative um alerta para ser avisado quando o total da compra cair abaixo da sua meta."
            }
          />

          <div
            style={{
              padding: 16,
              borderRadius: 20,
              border: "1px solid #fed7aa",
              background: "#fffdf8",
              display: "grid",
              gap: 12,
            }}
          >
            <div style={{ color: "#9a3412" }}>
              {(t?.("list.alerts.current_radius", "Raio atual") || "Raio atual") + ": "}
              {configuredDiscoveryRadiusKmLabel}.
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <input
                style={{ ...inputStyle, minWidth: 180 }}
                placeholder={
                  t?.("list.alerts.target_placeholder", "Total alvo ex: 89,90") ||
                  "Total alvo ex: 89,90"
                }
                value={shoppingListAlertTargetTotal}
                onChange={(event) => setShoppingListAlertTargetTotal(event.target.value)}
              />
              <input
                style={{ ...inputStyle, minWidth: 180 }}
                placeholder={
                  t?.("list.alerts.radius_placeholder", `Raio maximo (${distanceUnitLabel})`) ||
                  `Raio maximo (${distanceUnitLabel})`
                }
                value={shoppingListAlertMaxDistanceKm}
                onChange={(event) => setShoppingListAlertMaxDistanceKm(event.target.value)}
              />
              <button onClick={createShoppingListAlert} style={primaryButtonStyle}>
                {t?.("list.alerts.create", "Criar alerta da lista") || "Criar alerta da lista"}
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
            {shoppingListAlerts?.length > 0 ? (
              shoppingListAlerts.slice(0, 4).map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: "1px solid #fed7aa",
                    background: "#ffffff",
                  }}
                >
                  <strong style={{ display: "block" }}>
                    {alert.shopping_list_name || `Lista #${alert.shopping_list_id}`}
                  </strong>
                  <div style={{ marginTop: 8, color: "#7c2d12" }}>
                    {(t?.("list.alerts.target_total", "Meta total") || "Meta total") + ": "}
                    {formatCurrency(alert.target_total_price)}
                  </div>
                  <div style={{ marginTop: 6, color: "#9a3412" }}>
                    {(t?.("list.alerts.radius_label", "Raio") || "Raio") + ": "}
                    {alert.max_distance_km
                      ? formatDistance(alert.max_distance_km)
                      : t?.("list.alerts.any_distance", "qualquer distancia") || "qualquer distancia"}
                  </div>
                  <div style={{ marginTop: 6, color: "#9a3412" }}>
                    {alert.last_triggered_total
                      ? `${t?.("list.alerts.last_hit", "Ultimo acerto") || "Ultimo acerto"}: ${formatCurrency(alert.last_triggered_total)}${alert.last_triggered_at ? ` | ${formatCapturedAt(alert.last_triggered_at)}` : ""}`
                      : t?.("list.alerts.waiting", "Aguardando um mercado bater sua meta.") || "Aguardando um mercado bater sua meta."}
                  </div>
                  <button
                    onClick={() => removeShoppingListAlert(alert.id)}
                    style={{ ...ghostButtonStyle, marginTop: 12 }}
                  >
                    {t?.("list.alerts.remove", "Remover alerta") || "Remover alerta"}
                  </button>
                </div>
              ))
            ) : (
              <div style={{ color: "#9a3412" }}>
                {t?.("list.alerts.empty", "Nenhum alerta de lista ativo ainda.") ||
                  "Nenhum alerta de lista ativo ainda."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
